import { CampaignActive } from "./data";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useSignIn } from "@/hooks/useSignIn";
import { useWalletAddress } from "@/hooks/useWalletAddress";
import { toaster } from "../toaster";
import { Sheet } from "react-modal-sheet";
import Spinner from "../spinner";
import participant from "@/assets/images/participant.svg";

interface Props {
  item: CampaignActive;
}

export function CampaignItem(props: Props) {
  const { item } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n, t } = useTranslation();

  const walletAddress = useWalletAddress();

  const [isLimit, setLimit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const { signIn } = useSignIn();

  const handleJoin = useCallback(
    async (campaignCode: string) => {
      try {
        setLoading(true);

        if (!item) {
          toaster.warning("The campaign has ended.");
          setLoading(false);
          return;
        }
        if (item.totalParticipant >= item.maxParticipant) {
          setLimit(true);
          setLoading(false);
          return;
        }

        setLoading(false);
        navigate(`./${campaignCode}`);
      } catch (error: any) {
        const msg = error?.message || "Something went wrong, please try again";
        toaster.error(msg);
        setLoading(false);
      }
    },
    [navigate, walletAddress]
  );

  const handleLogin = useCallback(
    async (campaignCode: string) => {
      const info = await signIn();
      if (!info?.address) {
        return;
      }
      handleJoin(campaignCode);
    },
    [handleJoin, signIn]
  );

  const handleClose = useCallback(() => {
    setSearchParams((e) => {
      e.delete("id");
      return e;
    });
  }, []);

  const statusText = useMemo(() => {
    if (item.status === "active") {
      if (item.campaignCode === "loyal-holders-oro-wdd-dna-orb") {
        return `8000/10000`;
      }
      return `${item.totalParticipant}/${item.maxParticipant}`;
    }
    if (item.status === "end") {
      return `${item.maxParticipant}/${item.maxParticipant}`;
    }
    if (item.status === "upcoming") {
      return `0/${item.maxParticipant}`;
    }

    return null;
  }, [
    item.status,
    item.campaignCode,
    item.totalParticipant,
    item.maxParticipant,
  ]);

  return (
    <div className="flex flex-col gap-3 p-4 bg-[#0C0703] border border-[#24201C] rounded-2xl">
      <div className="relative rounded-2xl overflow-hidden">
        <div
          className="w-full h-[120px] flex items-stretch justify-stretch self-center"
          style={{
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "100% auto",
          }}
        >
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <ul className="flex flex-row gap-2 flex-wrap">
          {item.tag?.map((i) => (
            <li
              key={`tag-${i}`}
              className="border border-white rounded-full px-2 list-none text-white text-sm"
            >
              {i}
            </li>
          ))}
        </ul>

        <p className="text-white font-medium text-xl leading-8">{item.name}</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            setSearchParams((e) => {
              e.set("id", item.campaignCode);
              return e;
            });
          }}
          type="button"
          className="flex-1 h-10 px-6 text-sm font-medium bg-white/10 rounded-full text-white"
        >
          {t("detail")}
        </button>

        <button
          type="button"
          disabled={item.status !== "active"}
          className={clsx(
            "flex-1 h-10 px-6 flex items-center justify-center rounded-full min-w-[120px] disable:bg-white disable:opacity-0.5"
            // { hidden: item.status !== 'active' },
          )}
          style={{
            background:
              item.status !== "active"
                ? "#FFF"
                : "var(--Bera-Orange-Gradient, linear-gradient(90deg, #FEC537 0%, #F68703 100%))",
            color: "#000",
            opacity: item.status !== "active" ? 0.5 : 1,
          }}
          onClick={() => {
            if (item.status !== "active") {
              return;
            }

            if (item.totalParticipant >= item.maxParticipant) {
              setLimit(true);
              return;
            }

            if (!walletAddress) {
              handleLogin(item.campaignCode);
              return;
            }
            handleJoin(item.campaignCode);
          }}
        >
          {t("join")}
        </button>
      </div>

      <div className="flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row gap-1 items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <g clipPath="url(#clip0_19983_4992)">
              <path
                d="M10.8333 10.8333C11.7173 10.8333 12.5652 11.1845 13.1903 11.8096C13.8154 12.4348 14.1666 13.2826 14.1666 14.1667V15.4167C14.1666 15.7482 14.0349 16.0661 13.8005 16.3006C13.5661 16.535 13.2481 16.6667 12.9166 16.6667H2.91663C2.58511 16.6667 2.26716 16.535 2.03274 16.3006C1.79832 16.0661 1.66663 15.7482 1.66663 15.4167V14.1667C1.66663 13.2826 2.01782 12.4348 2.64294 11.8096C3.26806 11.1845 4.1159 10.8333 4.99996 10.8333H10.8333ZM15.8333 10.8333C16.4963 10.8333 17.1322 11.0967 17.6011 11.5656C18.0699 12.0344 18.3333 12.6703 18.3333 13.3333V14.5833C18.3333 14.9149 18.2016 15.2328 17.9672 15.4672C17.7328 15.7016 17.4148 15.8333 17.0833 15.8333H15.8333V14.1667C15.8338 13.5197 15.6834 12.8816 15.3941 12.3029C15.1048 11.7243 14.6845 11.2211 14.1666 10.8333H15.8333ZM7.91663 2.5C8.91119 2.5 9.86501 2.89509 10.5683 3.59835C11.2715 4.30161 11.6666 5.25544 11.6666 6.25C11.6666 7.24456 11.2715 8.19839 10.5683 8.90165C9.86501 9.60491 8.91119 10 7.91663 10C6.92206 10 5.96824 9.60491 5.26498 8.90165C4.56171 8.19839 4.16663 7.24456 4.16663 6.25C4.16663 5.25544 4.56171 4.30161 5.26498 3.59835C5.96824 2.89509 6.92206 2.5 7.91663 2.5ZM15 5C15.663 5 16.2989 5.26339 16.7677 5.73223C17.2366 6.20107 17.5 6.83696 17.5 7.5C17.5 8.16304 17.2366 8.79893 16.7677 9.26777C16.2989 9.73661 15.663 10 15 10C14.3369 10 13.701 9.73661 13.2322 9.26777C12.7634 8.79893 12.5 8.16304 12.5 7.5C12.5 6.83696 12.7634 6.20107 13.2322 5.73223C13.701 5.26339 14.3369 5 15 5Z"
                fill="#808080"
              />
            </g>
            <defs>
              <clipPath id="clip0_19983_4992">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>

          {statusText ? (
            <p className="text-[#808080] text-base">{statusText}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-1">
          <img
            src={participant}
            alt="participant"
            width={20}
            height={20}
            loading="lazy"
          />

          <p className="text-sm font-medium text-[#808080] truncate">
            {`${new Date(item.startTime).getUTCMonth()}/${new Date(
              item.startTime
            ).getUTCDate()}`}{" "}
            -{" "}
            {`${new Date(item.endTime).getUTCMonth()}/${new Date(
              item.endTime
            ).getUTCDate()}`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="fixed z-50 w-screen h-screen inset-0 items-center justify-center">
          <Spinner />
        </div>
      ) : null}

      {isLimit ? (
        <div
          id="limit-page"
          className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center"
        >
          <div className="px-4">
            <div className="w-full bg-[#0C0703] border border-[#24201C] rounded-2xl px-6 py-8 flex flex-col gap-2 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="57"
                height="56"
                viewBox="0 0 57 56"
                fill="none"
              >
                <g clipPath="url(#clip0_19983_9791)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M28.5 4.6665C41.387 4.6665 51.8333 15.1128 51.8333 27.9998C51.8333 40.8868 41.387 51.3332 28.5 51.3332C15.613 51.3332 5.16663 40.8868 5.16663 27.9998C5.16663 15.1128 15.613 4.6665 28.5 4.6665ZM35.3086 33.9988L35.2666 33.9615C34.8288 33.5102 34.3048 33.1514 33.7257 32.9062C33.1466 32.6611 32.5243 32.5347 31.8955 32.5345C31.2666 32.5343 30.6442 32.6602 30.0649 32.905C29.4857 33.1497 28.9615 33.5082 28.5233 33.9592C28.5204 33.9626 28.5169 33.9654 28.5128 33.9673C28.5088 33.9691 28.5044 33.9701 28.5 33.9701C28.4955 33.9701 28.4911 33.9691 28.4871 33.9673C28.483 33.9654 28.4795 33.9626 28.4766 33.9592C27.6391 33.0963 26.5027 32.5873 25.3012 32.5369C24.0998 32.4866 22.9247 32.8987 22.018 33.6885L21.7333 33.9615C21.696 33.9988 21.6493 34.0478 21.598 34.0408L21.5443 34.0152L20.744 33.2802C20.3069 32.8763 19.7321 32.6546 19.137 32.6605C18.542 32.6664 17.9717 32.8994 17.5427 33.312C17.1138 33.7245 16.8587 34.2853 16.8297 34.8797C16.8006 35.4741 16.9997 36.0571 17.3863 36.5095L17.5893 36.7195L18.392 37.4545C19.2554 38.2456 20.3799 38.6912 21.5509 38.7062C22.7219 38.7213 23.8574 38.3048 24.741 37.5362L25.049 37.2422C25.07 37.2165 25.1003 37.1792 25.1283 37.2072C26.9716 39.1112 30.0283 39.1112 31.8716 37.2072L31.8996 37.2025L31.965 37.2585C33.6683 39.0132 36.3983 39.1835 38.3 37.7135L38.608 37.4545L39.4106 36.7195C39.8482 36.3184 40.1164 35.7657 40.1608 35.1738C40.2052 34.5819 40.0223 33.9954 39.6494 33.5336C39.2765 33.0718 38.7416 32.7694 38.1537 32.6881C37.5657 32.6068 36.9689 32.7526 36.4846 33.0958L36.256 33.2802L35.4556 34.0152C35.402 34.0618 35.3506 34.0385 35.3086 33.9988ZM20.3333 18.6665C19.405 18.6665 18.5148 19.0353 17.8584 19.6916C17.202 20.348 16.8333 21.2382 16.8333 22.1665C16.8333 23.0948 17.202 23.985 17.8584 24.6414C18.5148 25.2978 19.405 25.6665 20.3333 25.6665C21.2616 25.6665 22.1518 25.2978 22.8082 24.6414C23.4645 23.985 23.8333 23.0948 23.8333 22.1665C23.8333 21.2382 23.4645 20.348 22.8082 19.6916C22.1518 19.0353 21.2616 18.6665 20.3333 18.6665ZM36.6666 18.6665C35.7384 18.6665 34.8481 19.0353 34.1918 19.6916C33.5354 20.348 33.1666 21.2382 33.1666 22.1665C33.1666 23.0948 33.5354 23.985 34.1918 24.6414C34.8481 25.2978 35.7384 25.6665 36.6666 25.6665C37.5949 25.6665 38.4851 25.2978 39.1415 24.6414C39.7979 23.985 40.1666 23.0948 40.1666 22.1665C40.1666 21.2382 39.7979 20.348 39.1415 19.6916C38.4851 19.0353 37.5949 18.6665 36.6666 18.6665Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_19983_9791">
                    <rect
                      width="56"
                      height="56"
                      fill="white"
                      transform="translate(0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>

              <p className="text-white text-lg leading-[26px] text-center">
                {t("campaign_reward_limit").replace(
                  "%s",
                  `${item.maxParticipant}`
                )}
              </p>

              <button
                type="button"
                className="px-6 py-3 flex items-center justify-center flex-1 rounded-full w-full mt-4"
                style={{
                  background:
                    "var(--Bera-Orange-Gradient, linear-gradient(90deg, #FEC537 0%, #F68703 100%))",
                  color: "#000",
                }}
                onClick={() => {
                  setLimit(false);
                }}
              >
                {t("ok_got_it")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Sheet
        isOpen={searchParams.get("id") === item.campaignCode}
        detent="content"
        onClose={() => handleClose()}
      >
        <Sheet.Container
          style={{
            background: "#22170E",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
          }}
        >
          <Sheet.Content>
            <div className="no-scrollbar">
              <div className="p-4">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover rounded-2xl mb-3"
                />
                <div className="flex items-center justify-between gap-2 mb-6">
                  <p className="text-white font-medium text-xl leading-8">
                    {item.name}
                  </p>

                  <div className="flex flex-row gap-1 items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_19983_4992)">
                        <path
                          d="M10.8333 10.8333C11.7173 10.8333 12.5652 11.1845 13.1903 11.8096C13.8154 12.4348 14.1666 13.2826 14.1666 14.1667V15.4167C14.1666 15.7482 14.0349 16.0661 13.8005 16.3006C13.5661 16.535 13.2481 16.6667 12.9166 16.6667H2.91663C2.58511 16.6667 2.26716 16.535 2.03274 16.3006C1.79832 16.0661 1.66663 15.7482 1.66663 15.4167V14.1667C1.66663 13.2826 2.01782 12.4348 2.64294 11.8096C3.26806 11.1845 4.1159 10.8333 4.99996 10.8333H10.8333ZM15.8333 10.8333C16.4963 10.8333 17.1322 11.0967 17.6011 11.5656C18.0699 12.0344 18.3333 12.6703 18.3333 13.3333V14.5833C18.3333 14.9149 18.2016 15.2328 17.9672 15.4672C17.7328 15.7016 17.4148 15.8333 17.0833 15.8333H15.8333V14.1667C15.8338 13.5197 15.6834 12.8816 15.3941 12.3029C15.1048 11.7243 14.6845 11.2211 14.1666 10.8333H15.8333ZM7.91663 2.5C8.91119 2.5 9.86501 2.89509 10.5683 3.59835C11.2715 4.30161 11.6666 5.25544 11.6666 6.25C11.6666 7.24456 11.2715 8.19839 10.5683 8.90165C9.86501 9.60491 8.91119 10 7.91663 10C6.92206 10 5.96824 9.60491 5.26498 8.90165C4.56171 8.19839 4.16663 7.24456 4.16663 6.25C4.16663 5.25544 4.56171 4.30161 5.26498 3.59835C5.96824 2.89509 6.92206 2.5 7.91663 2.5ZM15 5C15.663 5 16.2989 5.26339 16.7677 5.73223C17.2366 6.20107 17.5 6.83696 17.5 7.5C17.5 8.16304 17.2366 8.79893 16.7677 9.26777C16.2989 9.73661 15.663 10 15 10C14.3369 10 13.701 9.73661 13.2322 9.26777C12.7634 8.79893 12.5 8.16304 12.5 7.5C12.5 6.83696 12.7634 6.20107 13.2322 5.73223C13.701 5.26339 14.3369 5 15 5Z"
                          fill="#808080"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_19983_4992">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                    {statusText ? (
                      <p className="text-[#808080] text-base">{statusText}</p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col gap-2 mb-6">
                  <div className="flex items-center gap-3">
                    <p className="text-white font-medium text-base">
                      {t("Timeline")}
                    </p>

                    <CampaignStatus item={item} />
                  </div>

                  <div className="flex items-center gap-1">
                    <img
                      src={participant}
                      alt="participant"
                      width={20}
                      height={20}
                      loading="lazy"
                    />

                    <p className="text-sm font-medium text-[#808080] truncate">
                      {`${new Date(item.startTime).getUTCMonth()}/${new Date(
                        item.startTime
                      ).getUTCDate()}`}{" "}
                      -{" "}
                      {`${new Date(item.endTime).getUTCMonth()}/${new Date(
                        item.endTime
                      ).getUTCDate()}`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 mb-6">
                  <p className="text-base font-medium text-white">
                    {t("Description")}
                  </p>
                  <p className="text-sm font-medium text-[#808080] whitespace-pre-line">
                    {item.i18nDesc?.[i18n.language] || item.description}
                  </p>
                </div>

                {/* <div className="flex flex-col gap-2 mb-10">
                <p className="text-base font-medium text-white">Category</p>

                <ul className="flex flex-row gap-2 flex-wrap">
                  {item.tag?.map((i) => (
                    <li key={`tag-${i}`} className="border border-white rounded-full px-2 list-none text-white text-sm">
                      {i}
                    </li>
                  ))}
                </ul>
              </div> */}

                <div
                  className={clsx("w-fulll h-[1px] bg-[#24201C] -mx-4", {
                    hidden: item.status !== "active",
                  })}
                />

                <div
                  className={clsx("py-4", { hidden: item.status !== "active" })}
                >
                  <button
                    type="button"
                    disabled={item.status !== "active"}
                    className={clsx(
                      "w-full h-12 px-6 flex items-center justify-center rounded-full min-w-[120px] disable:bg-white disable:opacity-0.5",
                      { hidden: item.status !== "active" }
                    )}
                    style={{
                      background:
                        item.status !== "active"
                          ? "#FFF"
                          : "var(--Bera-Orange-Gradient, linear-gradient(90deg, #FEC537 0%, #F68703 100%))",
                      color: "#000",
                      opacity: item.status !== "active" ? 0.5 : 1,
                    }}
                    onClick={() => {
                      if (item.status !== "active") {
                        return;
                      }

                      handleClose();

                      if (item.totalParticipant >= item.maxParticipant) {
                        setLimit(true);
                        return;
                      }

                      handleJoin(item.campaignCode);
                    }}
                  >
                    {t("join")}
                  </button>
                </div>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop
          className="bg-black/70"
          onTap={() => {
            handleClose();
          }}
        />
      </Sheet>
    </div>
  );
}

export function CampaignStatus(props: { item: CampaignActive }) {
  const { t } = useTranslation();
  const status = useMemo(() => {
    return props.item.status;
  }, [props.item.status]);

  if (status === "end") {
    return (
      <p className="text-[#5A4E42] text-xs leading-4 border border-[#5A4E42] rounded-full px-[10px] py-1">
        {t("ended")}
      </p>
    );
  }

  if (status === "active") {
    return (
      <p className="text-[#F68703] text-xs leading-4 border border-[#F68703] rounded-full px-[10px] py-1">
        <span className="inline-block w-1 h-1 rounded-full bg-[#F68703] mr-2 mb-[2px]"></span>
        {t("on_going")}
      </p>
    );
  }

  return (
    <p className="text-white text-xs leading-4 border border-white rounded-full px-[10px] py-1">
      {t("up_coming")}
    </p>
  );
}
